import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, FileText, Image, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author: '',
    category: '',
    image_url: ''
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Error al cargar posts');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('session_token');
    setUploading(true);

    try {
      let imageUrl = formData.image_url;

      // Subir imagen si hay una nueva
      if (imageFile) {
        const imgFormData = new FormData();
        imgFormData.append('image', imageFile);
        
        const imgResponse = await axios.post(
          `${BACKEND_URL}/api/admin/site-config/upload-image`,
          imgFormData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              
            } 
          }
        );
        imageUrl = imgResponse.data.url;
      }

      const postData = { ...formData, image_url: imageUrl };

      if (editingPost) {
        await axios.put(
          `${BACKEND_URL}/api/admin/blog/${editingPost.post_id}`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Post actualizado');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/blog`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Post creado');
      }
      setIsOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar post');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('¿Estás segura de eliminar este post?')) return;

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post eliminado');
      fetchPosts();
    } catch (error) {
      toast.error('Error al eliminar post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setImageFile(null);
    setFormData({
      title: post.title,
      content: post.content,
      author: post.author,
      category: post.category,
      image_url: post.image_url || ''
    });
    setIsOpen(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setImageFile(null);
    setFormData({
      title: '',
      content: '',
      author: '',
      category: '',
      image_url: ''
    });
  };

  return (
    <div className="min-h-screen bg-[#09090b] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center mb-12">
          <h1 className="text-4xl font-bold" style={{fontFamily: 'Poppins, sans-serif'}}>
            Gestión de Blog
          </h1>
          <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-black hover:bg-zinc-800 text-white rounded-full px-6 py-3">
                <Plus className="w-5 h-5 mr-2" />
                Crear Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPost ? 'Editar Post' : 'Crear Nuevo Post'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Título</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Autor</Label>
                    <Input
                      value={formData.author}
                      onChange={(e) => setFormData({...formData, author: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Categoría</Label>
                    <Input
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Contenido</Label>
                  <textarea
                    className="w-full p-2 border rounded-md font-serif"
                    rows={12}
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    required
                  />
                </div>

                {/* Subir Imagen de Portada */}
                <div>
                  <Label>🖼️ Imagen del Artículo</Label>
                  {formData.image_url && !imageFile && (
                    <div className="mb-2 flex items-center gap-3 bg-blue-50 rounded-lg p-2">
                      <img 
                        src={formData.image_url.startsWith('/api') ? `${BACKEND_URL}${formData.image_url}` : formData.image_url}
                        alt="Imagen actual"
                        className="w-16 h-16 object-cover rounded"
                      />
                      <span className="text-xs text-blue-700">Imagen actual</span>
                    </div>
                  )}
                  <div 
                    onClick={() => imageInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                      imageFile ? 'border-blue-300 bg-blue-50' : 'border-zinc-700 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setImageFile(file);
                      }}
                      className="hidden"
                    />
                    {imageFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <Check className="w-6 h-6 text-blue-600" />
                        <div className="text-left">
                          <p className="font-medium text-blue-700 text-sm">{imageFile.name}</p>
                          <p className="text-xs text-blue-600">{formatFileSize(imageFile.size)}</p>
                        </div>
                        <button 
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setImageFile(null); }}
                          className="ml-2 text-red-500 hover:text-red-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Image className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
                        <p className="text-sm text-zinc-400">
                          {formData.image_url ? 'Cambiar imagen' : 'Subir imagen'}
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">JPG, PNG o WebP (máx 5MB)</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsOpen(false); resetForm(); }}>
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-black hover:bg-zinc-800"
                    disabled={uploading}
                  >
                    {uploading ? 'Subiendo...' : (editingPost ? 'Actualizar' : 'Crear')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {posts.map((post) => (
            <div key={post.post_id} className="workout-card rounded-3xl p-6">
              <div className="flex gap-6">
                {post.image_url && (
                  <img 
                    src={post.image_url.startsWith('/api') ? `${BACKEND_URL}${post.image_url}` : post.image_url} 
                    alt={post.title} 
                    className="w-48 h-32 object-cover rounded-2xl" 
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-bold text-pink-600 uppercase">{post.category}</span>
                    <span className="text-sm text-zinc-500">{new Date(post.created_at).toLocaleDateString('es-ES')}</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{fontFamily: 'Poppins, sans-serif'}}>{post.title}</h3>
                  <p className="text-sm text-zinc-400 mb-4">Por {post.author}</p>
                  <p className="text-zinc-300 mb-4 line-clamp-2">{post.content}</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(post)}
                      variant="outline"
                      size="sm"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      onClick={() => handleDelete(post.post_id)}
                      variant="destructive"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminBlog;
